import React, { useState } from "react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * One-time community policy dialog shown before a user makes their first
 * lounge post or comment. Persisted server-side on the user record
 * (`accepted_lounge_terms_at`), so it survives across devices.
 */
export default function LoungeTermsDialog({ onAgree, onClose }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/auth/accept-lounge-terms");
      toast.success("Welcome to the lounge ✦");
      onAgree?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="lounge-terms-dialog"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3"
    >
      <div className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 rounded-t-2xl flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" />
          <span className="font-hand text-xl">lounge community policy</span>
        </div>
        <div className="p-4 space-y-3">
          <p className="font-serif-it text-tb-navy text-lg leading-snug">
            Before you share your first story, please agree to a few ground rules.
          </p>
          <div className="rounded-xl bg-tb-cream-2 border-2 border-tb-cream-3 p-3 space-y-2 text-sm font-ui text-tb-navy/85">
            <p>
              The TravelBio lounge is a kind, public space for travellers of every age.
              By posting, commenting or sending messages, you agree <b>not</b> to share
              content that includes:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Nudity</b> or sexual content of any kind</li>
              <li><b>Violence</b>, threats, gore, or anything glorifying harm</li>
              <li><b>Hate speech</b> or slurs directed at any group
                (race, religion, gender, sexuality, disability, nationality)</li>
              <li>Spam, scams or impersonation</li>
            </ul>
            <p>
              Posts must be appropriate and follow the full policy on our{" "}
              <a
                href="/policies"
                target="_blank"
                rel="noreferrer"
                className="text-tb-blue underline hover:text-tb-navy"
              >
                Policies &amp; Disclosures
              </a>{" "}
              page.
            </p>
            <p className="text-tb-coral italic flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Anyone who breaks these rules will be flagged for review, and
              accounts found guilty may be removed from the lounge — or from
              TravelBio entirely.
            </p>
          </div>
          <label className="flex items-start gap-2 text-sm font-ui text-tb-navy/85 cursor-pointer">
            <input
              type="checkbox"
              data-testid="lounge-terms-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 accent-tb-navy"
            />
            <span>I have read and agree to follow these community guidelines.</span>
          </label>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              data-testid="lounge-terms-cancel"
              className="flex-1 py-2.5 rounded-xl bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap"
            >
              not yet
            </button>
            <button
              type="button"
              disabled={!checked || busy}
              onClick={accept}
              data-testid="lounge-terms-agree"
              className="flex-1 py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-50"
            >
              {busy ? "saving…" : "I agree"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
